import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import Sidebar from '@/components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Clock, Plus, RefreshCw, Check, X as XIcon, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { canEdit } from '@/lib/permissions';

const STATUS_COLORS = {
  draft: 'bg-slate-200 text-slate-700',
  submitted: 'bg-blue-100 text-blue-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
  invoiced: 'bg-purple-100 text-purple-700',
};

const fmtMoney = (n, currency = 'AUD') => new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(n || 0);

export default function Timesheets() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [entries, setEntries] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    photographer_id: searchParams.get('photographer_id') || '',
    event_id: searchParams.get('event_id') || '',
    status: searchParams.get('status') || '',
    work_date_from: searchParams.get('from') || '',
    work_date_to: searchParams.get('to') || '',
  });
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ photographer_id: '', event_id: '', spot_id: '', work_date: new Date().toISOString().slice(0, 10), start_time: '', end_time: '', hours: '', note: '', billable: true });
  const [autoEventId, setAutoEventId] = useState('');

  const canManage = canEdit(user, 'timesheets');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [tRes, pRes, eRes] = await Promise.all([
        api.get('/timesheets', { params }),
        api.get('/photographers'),
        api.get('/events'),
      ]);
      setEntries(tRes.data || []);
      setPhotographers(pRes.data || []);
      setEvents(eRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const updateFilter = (k, v) => {
    const next = { ...filters, [k]: v };
    setFilters(next);
    const sp = new URLSearchParams();
    Object.entries(next).forEach(([key, val]) => { if (val) sp.set(key, val); });
    setSearchParams(sp);
  };

  const totals = useMemo(() => ({
    hours: entries.reduce((s, e) => s + (e.hours || 0), 0),
    cost: entries.reduce((s, e) => s + (e.cost || 0), 0),
    revenue: entries.reduce((s, e) => s + ((e.bill_rate || 0) * (e.hours || 0)), 0),
    count: entries.length,
  }), [entries]);

  const setStatus = async (entry, status) => {
    try {
      await api.patch(`/timesheets/${entry.id}`, { status });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update');
    }
  };

  const remove = async (entry) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await api.delete(`/timesheets/${entry.id}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete');
    }
  };

  const create = async () => {
    if (!createForm.photographer_id || !createForm.event_id) {
      toast.error('Photographer and event are required');
      return;
    }
    try {
      await api.post('/timesheets', { ...createForm, hours: createForm.hours ? parseFloat(createForm.hours) : undefined });
      toast.success('Entry created');
      setShowCreate(false);
      setCreateForm({ photographer_id: '', event_id: '', spot_id: '', work_date: new Date().toISOString().slice(0, 10), start_time: '', end_time: '', hours: '', note: '', billable: true });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create');
    }
  };

  const autoGenerate = async () => {
    if (!autoEventId) {
      toast.error('Select an event first');
      return;
    }
    try {
      const r = await api.post(`/events/${autoEventId}/timesheets/auto-generate`);
      toast.success(`Created ${r.data.created} entries from check-ins`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="md:ml-64 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Clock className="w-7 h-7 text-emerald-600" />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">Timesheets</h1>
                <p className="text-sm text-slate-500">Photographer hours per event. Auto-generate from spot check-ins or add manually. Approved entries flow into invoices.</p>
              </div>
            </div>
            {canManage && <Button onClick={() => setShowCreate(true)}><Plus className="w-4 h-4 mr-1" /> New entry</Button>}
          </div>

          <Card className="glass-card p-4 mb-4">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
              <div>
                <Label className="text-xs">Photographer</Label>
                <Select value={filters.photographer_id || 'all'} onValueChange={(v) => updateFilter('photographer_id', v === 'all' ? '' : v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All</SelectItem>{photographers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Event</Label>
                <Select value={filters.event_id || 'all'} onValueChange={(v) => updateFilter('event_id', v === 'all' ? '' : v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All</SelectItem>{events.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={filters.status || 'all'} onValueChange={(v) => updateFilter('status', v === 'all' ? '' : v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="invoiced">Invoiced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">From</Label><Input type="date" value={filters.work_date_from} onChange={(e) => updateFilter('work_date_from', e.target.value)} /></div>
              <div><Label className="text-xs">To</Label><Input type="date" value={filters.work_date_to} onChange={(e) => updateFilter('work_date_to', e.target.value)} /></div>
            </div>
          </Card>

          {canManage && (
            <Card className="glass-card p-4 mb-4">
              <p className="text-xs text-slate-500 mb-2">Auto-generate from spot check-ins (only spots with both arrive and leave recorded):</p>
              <div className="flex gap-2 flex-wrap items-end">
                <Select value={autoEventId} onValueChange={setAutoEventId}>
                  <SelectTrigger className="w-72"><SelectValue placeholder="Pick an event…" /></SelectTrigger>
                  <SelectContent>{events.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} ({e.date})</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" onClick={autoGenerate} disabled={!autoEventId}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Auto-generate</Button>
              </div>
            </Card>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Card className="glass-card p-4"><p className="text-xs text-slate-500">Entries</p><p className="text-2xl font-bold">{totals.count}</p></Card>
            <Card className="glass-card p-4"><p className="text-xs text-slate-500">Hours</p><p className="text-2xl font-bold">{totals.hours.toFixed(2)}</p></Card>
            <Card className="glass-card p-4"><p className="text-xs text-slate-500">Cost</p><p className="text-2xl font-bold text-amber-700">{fmtMoney(totals.cost)}</p></Card>
            <Card className="glass-card p-4"><p className="text-xs text-slate-500">Billable</p><p className="text-2xl font-bold text-emerald-700">{fmtMoney(totals.revenue)}</p></Card>
          </div>

          {loading ? <p className="text-center py-8 text-slate-500">Loading…</p> : entries.length === 0 ? (
            <Card className="glass-card p-12 text-center text-slate-500"><Clock className="w-12 h-12 mx-auto mb-3 opacity-40" />No timesheet entries yet.</Card>
          ) : (
            <Card className="glass-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b text-xs text-slate-600 text-left">
                  <tr><th className="px-3 py-2">Date</th><th className="px-3 py-2">Photographer</th><th className="px-3 py-2">Event</th><th className="px-3 py-2">Spot</th><th className="px-3 py-2">Time</th><th className="px-3 py-2">Hours</th><th className="px-3 py-2">Rate</th><th className="px-3 py-2">Cost</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Source</th><th className="px-3 py-2"></th></tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-3 py-2 text-xs">{e.work_date}</td>
                      <td className="px-3 py-2">{e.photographer_name}</td>
                      <td className="px-3 py-2 text-xs">{events.find((ev) => ev.id === e.event_id)?.name || '—'}</td>
                      <td className="px-3 py-2 text-xs">{e.spot_name || '—'}</td>
                      <td className="px-3 py-2 text-xs">{e.start_time && e.end_time ? `${e.start_time} – ${e.end_time}` : '—'}</td>
                      <td className="px-3 py-2 font-medium">{(e.hours || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-xs">{fmtMoney(e.hourly_rate || 0)}/hr</td>
                      <td className="px-3 py-2 font-medium">{fmtMoney(e.cost || 0)}</td>
                      <td className="px-3 py-2"><span className={`text-[11px] px-2 py-0.5 rounded ${STATUS_COLORS[e.status] || 'bg-slate-100'}`}>{e.status}</span></td>
                      <td className="px-3 py-2 text-[11px] text-slate-500">{e.source}</td>
                      <td className="px-3 py-2">
                        {canManage && (
                          <div className="flex gap-1">
                            {e.status !== 'approved' && e.status !== 'invoiced' && <button onClick={() => setStatus(e, 'approved')} className="text-emerald-600 hover:text-emerald-700" title="Approve"><Check className="w-3.5 h-3.5" /></button>}
                            {e.status !== 'rejected' && e.status !== 'invoiced' && <button onClick={() => setStatus(e, 'rejected')} className="text-rose-600 hover:text-rose-700" title="Reject"><XIcon className="w-3.5 h-3.5" /></button>}
                            <button onClick={() => remove(e)} className="text-slate-400 hover:text-red-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      </main>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New timesheet entry</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Photographer *</Label>
              <Select value={createForm.photographer_id} onValueChange={(v) => setCreateForm({ ...createForm, photographer_id: v })}>
                <SelectTrigger><SelectValue placeholder="Pick…" /></SelectTrigger>
                <SelectContent>{photographers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Event *</Label>
              <Select value={createForm.event_id} onValueChange={(v) => setCreateForm({ ...createForm, event_id: v })}>
                <SelectTrigger><SelectValue placeholder="Pick…" /></SelectTrigger>
                <SelectContent>{events.map((ev) => <SelectItem key={ev.id} value={ev.id}>{ev.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" value={createForm.work_date} onChange={(e) => setCreateForm({ ...createForm, work_date: e.target.value })} /></div>
            <div><Label>Hours (optional)</Label><Input type="number" step="0.25" value={createForm.hours} onChange={(e) => setCreateForm({ ...createForm, hours: e.target.value })} placeholder="auto from times" /></div>
            <div><Label>Start</Label><Input type="time" value={createForm.start_time} onChange={(e) => setCreateForm({ ...createForm, start_time: e.target.value })} /></div>
            <div><Label>End</Label><Input type="time" value={createForm.end_time} onChange={(e) => setCreateForm({ ...createForm, end_time: e.target.value })} /></div>
            <div className="col-span-2"><Label>Note</Label><Input value={createForm.note} onChange={(e) => setCreateForm({ ...createForm, note: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={create}>Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
